import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DFORM, NODE_BASE, NODE_CHILD, NODE_PARENT, PATH, URL_GET_ALL } from '@core/utils/constant';
import * as _ from 'lodash';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, map, take, tap } from 'rxjs/operators';
import { MenuService } from '../bootstrap/menu.service';
import { URL_LOGIN } from '../utils/constant';
import { DformDialogService } from './../../shared/components/dform-dialogs/dialog.service';
import { LocalStoreEnum } from './../../shared/models/enum/local-store.enum';
import { MenyType } from './../utils/constant';
import { ActivityIndicatorSingletonService } from './activity-indicator-singleton.service';
import { DialogManagementService } from './dialog-management.service';
import { HttpClientService } from './httpclient.service';
import { JwtHelper } from './jwt-helper';
import { LocalStoreManagerService } from './local-store-manager.service';
import { Login } from './model/login.dto';
import { HttpOptions } from './model/request.base.dto';
import { HttpInterface } from './model/response.base.dto';
import { MessageSeverity, ToastService } from './toast.service';
import { CATALOG, reloadCatalog } from './utils.service';
import {TranslateService} from "@ngx-translate/core";

/**
 * description: Xử lý thông tin token và catalog
 * @author bondm
 * date: 1/10/2020
 */

@Injectable()
export class AuthenticationService {
  private readonly STORAGE: string[] = ['JWT_ADMIN', 'RJWT_ADMIN', 'UI_ADMIN', 'TE_ADMIN'];
  constructor(
    private router: Router,
    private httpClient: HttpClientService,
    private localStore: LocalStoreManagerService,
    private dialogManagementService: DialogManagementService,
    private menu: MenuService,
    private dialogService: DformDialogService,
    private indicator: ActivityIndicatorSingletonService,
    private toastr: ToastService,
    private translateService: TranslateService
  ) { }

  /**
   * Xử lý login vào ứng dụng lưu token
   * @param authentication;
   */
  public login(authentication: string) {
    if (authentication) {
      const headers = new HttpHeaders({
        Authorization: `Basic ${authentication}`,
      });
      const opstions: HttpOptions = {
        path: PATH.GENERATE_TOKEN,
        url: URL_LOGIN,
        headers,
        params: {
          grantType: 'PASSWORD_GRANT',
        },
        isAuthentication: false
      };
      return this.httpClient
        .get(opstions)
        .pipe(
          take(1),
          tap((response: HttpInterface<Login>) => this.storeSession(response.data)),
          map((response: HttpInterface<Login>) => {
            return response;
          })
        );
    }
  }

  /**
   * @description Lưu chữ thông tin liên quan đến session của người dùng
   * Lưu thông tin người dùng
   * Lưu thông tin sessionId
   * Lưu thông tin của menulist trả về khi login
   * Lưu thông tin catalog
   * Lưu thời gian token
   */
  storeSession(data: Login, isRefresh = true) {
    const jwtHelper = new JwtHelper();
    const decodedAccessToken = jwtHelper.decodeToken(data.accessToken);
    this.setToken(data.accessToken);
    this.setRefreshToken(data.refreshToken);
    this.setTimeExpiration(data.timeExpiration);
    if (isRefresh) {
      Object.keys(CATALOG).forEach(v => CATALOG[v] = []);
      this.setUserToLocalStore(decodedAccessToken);
      const menus = data.info.menus.filter((val: any) => val.type === MenyType.SYSTEM);
      this.setMenuListToLocalStore(menus, LocalStoreEnum.Menu_List);
      this.mapMenuAdmin(menus, null, 'setting');
    }
  }

  mapMenuAdmin(menuServer, parentId, namespace, node = NODE_BASE) {
    let menusEsb: any = [];
    const menuChild = menuServer.filter(x => x.parentId === parentId);
    (menuChild || []).forEach(element => {
      if (element.parentId == null && element.url) {
        const menusDform = this.mapMenuAdmin(menuServer, element.id, DFORM, NODE_PARENT);
        this.setMenuListToLocalStore(menusDform, LocalStoreEnum.Menu_List);
      }
      const url = namespace === '' ? element.url : `${namespace}/${element.url}`;
      const menu: any = {
        id: element.id,
        route: url,
        name: element.name,
        parentId: node === NODE_PARENT ? 0 : element.parentId,
        position: element.position,
        icon: element.icon,
        active: element.active,
        activeService: element.activeService,
        type: element.type
      };
      const children = this.mapMenuAdmin(menuServer, element.id, url, NODE_CHILD);
      if (children && children.length > 0) {
        const menuSubChild = menuServer.filter(x => x.parentId === element.id && x.active);
        if (menuSubChild.length > 0) {
          menu.route = null;
        }
        menusEsb = [...menusEsb, ...children];
      }
      menusEsb.push(menu);
    });
    return menusEsb;
  }

  private setUserToLocalStore(user: any) {
    this.localStore.savePermanentData(user, LocalStoreEnum.User_Infor);
  }

  private setToken(token: string) {
    this.localStore.savePermanentData(token, LocalStoreEnum.Token);
  }

  private setRefreshToken(token: string) {
    if (token) {
      this.localStore.savePermanentData(token, LocalStoreEnum.RefreshToken);
    }
  }

  private setTimeExpiration(time: number) {
    this.localStore.savePermanentData(time / 1000 - 15, LocalStoreEnum.Time_Expiration);
    this.localStore.savePermanentData(new Date(), LocalStoreEnum.Date_Expiration);
  }

  private setMenuListToLocalStore(menuList: any[], key = LocalStoreEnum.Menu_List) {
    this.localStore.savePermanentData(menuList, key);
  }

  /**
   * Kiểm tra tồn tại localStore
   */
  private isStoreDataValid(): boolean {

    // Kiểm tra các biến môi trường token, refreshtoken, user_info
    for (const x of this.STORAGE) {
      if (!this.localStore.exists(x)) {
        return false;
      }
    }

    // Kiểm tra được phân quyền menu chưa
    if (!this.localStore.exists(LocalStoreEnum.Menu_List) && !this.localStore.exists(LocalStoreEnum.Menu_List_EMB)) {
      return false;
    }

    return true;
  }

  /**
   * Determines whether login is
   * @returns true if logged in with unexpired EA
   */
  tokenValid(): boolean {
    return this.isStoreDataValid();
  }


  /**
   * Xử lý refresh tocken theo thời gian
   */
  refreshToken = () => {
    const opstions: HttpOptions = {
      path: PATH.GENERATE_TOKEN,
      url: URL_LOGIN,
      params: {
        grantType: 'REFRESH_TOKEN',
        token: this.localStore.getData(LocalStoreEnum.RefreshToken),
      },
      isAuthentication: false
    };
    return this.httpClient.get(opstions)
      .pipe(
        take(1),
        tap((response: any) => this.storeSession(response.data, false)),
        map((response: any) => {
          return response;
        }),
        catchError(_ => of({ status: 500 }))
      );
  };

  /**
   * Xử lý đăng xuất tài khoản
   */
  public logout() {
    const opstions: HttpOptions = {
      path: PATH.REVOKE_TOKEN,
      url: URL_LOGIN,
      params: {
        token: this.localStore.getData(LocalStoreEnum.RefreshToken)
      }
    };
    this.indicator.showActivityIndicator()
    this.httpClient.delete(opstions)
      .pipe(
        take(1),
        finalize(() => this.indicator.hideActivityIndicator())
      ).subscribe(res => {
      this.clearData();
      const config = {
        positionClass: 'toast-bottom-right',
        timeOut: 1000,
        extendedTimeOut: 1000
      };
      this.toastr.showToastr(
        this.translateService.instant('LOGIN.LOGOUT_SUCCESS'),
        this.translateService.instant('dialog.notification'),
        MessageSeverity.success,
        config
      );
    }, (error) => {
      const message = error.error && error?.error?.message ? error?.error?.message : this.translateService.instant('error.ERROR_IT_SUPPORT');
      const uuid = error.error && error.error?.uuid ? error.error?.uuid : null;
      this.dialogService.error({
        title: 'dialog.notification',
        innerHTML: uuid ? `${message} <br> ${uuid}` : `${message}`
      }, result => { if (result) { } });
    });
  }

  public clearData(isClearStorage = true) {
    this.dialogManagementService.closeAll();
    this.menu.reset();
    Object.keys(CATALOG).forEach(v => CATALOG[v] = [])
    if (isClearStorage) {
      this.clearClientStorage();
      this.router.navigate(['/admin/login']);
    } else {
      this.clearClientToken();
      this.router.navigate(['/admin/login'], { queryParams: { returnUrl: this.router.url } });
    }
  }

  /**
   * clear thông tin lưu trữ ở local
   */
  clearClientStorage() {
    this.localStore.clearAllStorage();
  }

  clearClientToken() {
    this.localStore.deleteData(LocalStoreEnum.RefreshToken);
    this.localStore.deleteData(LocalStoreEnum.Token);
  }

  // Kiểm tra xem đủ localstore chưa. Nếu chưa có thì gọi lại
  public loadCatalog(): Promise<boolean> {
    return new Promise(
      (resolve, reject) => {
        const listCatalog = URL_GET_ALL.filter(x => !this.localStore.exists(x.value));
        const checkCatalog: any = _.transform(CATALOG, function (result, value, key) {
          if (value && CATALOG[key].length > 0) {
            result[key] = value;
          }
        })
        if (_.isEmpty(checkCatalog)) {
          URL_GET_ALL.forEach((val, index) => {
            reloadCatalog(URL_GET_ALL[index].value);
          })
        }

        if (listCatalog && listCatalog.length > 0) {
          this.indicator.showActivityIndicator()
          this.reloadCacheCatalog(listCatalog).pipe(
            finalize(() => this.indicator.hideActivityIndicator()),
          ).subscribe((result: Array<any>) => {
            result.map((val, index) => {
              if (val && val.data) {
                this.localStore.savePermanentData(val.data, listCatalog[index].value);
                reloadCatalog(listCatalog[index].value);
              }
            });
            resolve(true);
          }, (err) => {
            const config = {
              positionClass: 'toast-bottom-right',
              timeOut: 3000,
              extendedTimeOut: 3000
            };
            this.toastr.showToastr(
              this.translateService.instant('error.ERROR_GET_CATEGORIES'),
              this.translateService.instant('dialog.notification'),
              MessageSeverity.error,
              config
            );
            this.router.navigate(['/admin/login']);
            resolve(false);
          });
        } else {
          resolve(true);
        }
      }
    );
  }

  // Gọi lại api load catalog chưa có trên localstore
  private reloadCacheCatalog(listCatalog) {
    return forkJoin(listCatalog.map(data => {
      if (data.key === 'GET_ALL_RM') {
        return this.httpClient.get({ url: data.url, path: PATH.SYSTEM[data.key], params: { siteCode: this.localStore.getData(LocalStoreEnum.User_Infor).site_code } }).pipe(map(res => res));
      } else {
        return this.httpClient.get({ url: data.url, path: PATH.SYSTEM[data.key] }).pipe(map(res => res));
      }
    }));
  }
}
